import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useState, useCallback, useEffect } from 'react';
import PinDropIcon from '@material-ui/icons/PinDrop';
import AddIcon from '@material-ui/icons/Add';
import { notify } from 'reducers/NotifierReducer';
import { useDispatch } from 'react-redux';
import { RMSAutocomplete } from '../../../shared/components/form-controls';
import generalServices, { getAddressById } from '../../services/generalServices';
import { executeApiAction } from '../../utility/apiHelper';
import { isEmpty } from 'features/shared/utility/helper';
import { RMSIconButton } from '../../components/buttons/index';
import { useAddressDialog } from 'features/user/hooks/useAddressDialog';

const SearchAddressAutoComplete = ({
  name,
  label,
  placeholder,
  onSelectionChange,
  onAddressUpdated,
  defaultAddressId = null,
  defaultAddressSelectedCallback = null,
  showAddIcon = true,
  onAddIconClick = () => {},
  showDetailsIcon = true,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const { openAddressDialog } = useAddressDialog();

  const addressPerms = usePermissions(PERMISSION_RESOURCES.ADDRESS);
  const canCreateAddress = addressPerms.Create;
  const canViewAddressDetails = Boolean(addressPerms.Read || addressPerms.Edit);

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchAddresses = async (searchText) => {
    await executeApiAction({
      setLoading,
      action: () => generalServices.getAddressesBySearch(searchText),
      onSuccess: (response) => setAddresses(response),
      onError: (error) => {
        dispatch(notify(error.message, 'error'));
      },
    });
  };

  const handleDefaultAddress = useCallback(
    async (addressId) => {
      if (!addressId) return;

      await executeApiAction({
        setLoading,
        dispatch,
        action: () => getAddressById(addressId),
        onSuccess: (response) => {
          setAddresses([response]);
          defaultAddressSelectedCallback?.(response);
        },
      });
    },
    [dispatch, defaultAddressSelectedCallback]
  );

  useEffect(() => {
    if (!isEmpty(defaultAddressId)) {
      handleDefaultAddress(defaultAddressId);
    }
  }, [defaultAddressId, handleDefaultAddress]);

  const handleAddressUpdated = useCallback(
    (address) => {
      if (!address) return;

      setAddresses((prev) =>
        prev.map((existing) =>
          existing.ptsAddressID === address.ptsAddressID ? { ...existing, ...address } : existing
        )
      );

      (onAddressUpdated || onSelectionChange)?.(address);
    },
    [onAddressUpdated, onSelectionChange]
  );

  return (
    <>
      <RMSAutocomplete
        name={name}
        label={label ?? 'Address'}
        placeholder={placeholder}
        options={addresses}
        optionLabel="FullAddressText"
        optionValue="ptsAddressID"
        getOptionLabel={(option) => option?.FullAddressText || ''}
        onSearch={searchAddresses}
        loading={loading}
        disabled={disabled}
        onSelectionChange={onSelectionChange}
        leftContent={() =>
          showAddIcon &&
          canCreateAddress && (
            <RMSIconButton
              icon={<AddIcon fontSize="small" />}
              tooltip="Add Address"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onAddIconClick?.(event);
              }}
            />
          )
        }
        rightContent={({ value }) =>
          showDetailsIcon &&
          value &&
          canViewAddressDetails && (
            <RMSIconButton
              icon={<PinDropIcon />}
              tooltip="Address Details"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                openAddressDialog(value.ptsAddressID, {
                  onAddressUpdated: handleAddressUpdated,
                });
              }}
            />
          )
        }
      />
    </>
  );
};

export default SearchAddressAutoComplete;
